/**
 * supabase/client.js — Singleton del cliente, constantes de conexión,
 * auth (signIn/signOut/getCurrentUser), roles/acceso y caches de resolución
 * (asesor/campus). Parte de la fachada src/lib/supabase.js.
 */

import { createClient } from "@supabase/supabase-js";
import { SUPER_ADMIN_EMAILS } from "../roles";

/** Emails con acceso total al pipeline (admin general + super admins). */
const FULL_ACCESS_EMAILS = new Set([
  "admin@tepeyac.mx",
  ...Array.from(SUPER_ADMIN_EMAILS || []),
]);

/**
 * ¿Tiene acceso total al pipeline (ve todos los leads, sin filtro de asesor)?
 *
 * Reglas:
 *   1. Email está en FULL_ACCESS_EMAILS (super admins + admin general hardcoded).
 *   2. El asesor tiene `rol === "admin"` en la tabla `asesores`.
 *
 * Esto permite dar acceso total a nuevos administradores con solo cambiar su
 * `rol` en la BD, sin tocar código.
 */
export async function _hasFullAccess(email) {
  if (!email) return false;
  const key = String(email).toLowerCase().trim();
  if (FULL_ACCESS_EMAILS.has(key)) return true;
  const rol = await _getAsesorRoleByEmail(key);
  return rol === "admin";
}

// ═══════════════════════════════════════════════════
// CLIENT SINGLETON
// ═══════════════════════════════════════════════════

/** @type {import("@supabase/supabase-js").SupabaseClient|null} */
let _client = null;

/** @returns {import("@supabase/supabase-js").SupabaseClient} */
export function getSupabase() {
  if (_client) return _client;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !key) {
    console.warn(
      "[supabase] Faltan VITE_SUPABASE_URL o VITE_SUPABASE_ANON_KEY",
    );
  }

  _client = createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
      storageKey: "crm-tepeyac-auth",
      flowType: "pkce",
    },
  });
  return _client;
}

export const supabase = getSupabase();

// ═══════════════════════════════════════════════════
// CACHES (in-memory, session-scoped)
// ═══════════════════════════════════════════════════

const _cache = {
  asesorId: new Map(),
  campusId: new Map(),
  asesorEmail: new Map(),
  asesorRol: new Map(),
};

/**
 * Resuelve el `rol` de un asesor por email. Cachea resultado.
 * @param {string|null} email
 * @returns {Promise<string|null>}  ej. "admin", "asesor", o null si no existe
 */
async function _getAsesorRoleByEmail(email) {
  const key = String(email || "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  if (_cache.asesorRol.has(key)) return _cache.asesorRol.get(key);

  const { data } = await getSupabase()
    .from("asesores")
    .select("rol")
    .ilike("email", key)
    .maybeSingle();

  const rol = data?.rol ?? null;
  _cache.asesorRol.set(key, rol);
  return rol;
}

/**
 * Resuelve el email de un asesor por su UUID. Cachea resultado.
 * @param {string|null} id
 * @returns {Promise<string|null>} email del asesor
 */
export async function getAsesorEmailById(id) {
  if (!id) return null;
  if (_cache.asesorEmail.has(id)) return _cache.asesorEmail.get(id);

  const { data } = await getSupabase()
    .from("asesores")
    .select("email")
    .eq("id", id)
    .maybeSingle();

  const email = data?.email ?? null;
  _cache.asesorEmail.set(id, email);
  return email;
}

export const _isUuid = (v) =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    v,
  );

/**
 * Resuelve asesor UUID por email. Cachea resultado.
 * @param {string|null} email
 * @returns {Promise<string|null>} asesor UUID
 */
export async function getAsesorIdByEmail(email) {
  const key = String(email || "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  if (_cache.asesorId.has(key)) return _cache.asesorId.get(key);

  const { data } = await getSupabase()
    .from("asesores")
    .select("id")
    .ilike("email", key)
    .maybeSingle();

  const id = data?.id ?? null;
  _cache.asesorId.set(key, id);
  return id;
}

/**
 * Resuelve campus UUID por código o nombre.
 * @param {string|null} value
 * @returns {Promise<string|null>}
 */
export async function _getCampusId(value) {
  if (!value) return null;
  if (_isUuid(value)) return value;

  const key = String(value).trim().toLowerCase();
  if (!key) return null;
  if (_cache.campusId.has(key)) return _cache.campusId.get(key);

  const { data } = await getSupabase()
    .from("campus")
    .select("id")
    .or(`codigo.ilike.${key},nombre.ilike.${key}`)
    .maybeSingle();

  const id = data?.id ?? null;
  _cache.campusId.set(key, id);
  return id;
}

// ═══════════════════════════════════════════════════
// AUTH (pendiente migración a Supabase Auth)
// ═══════════════════════════════════════════════════

/**
 * Resuelve un asesor por email y lo convierte en el objeto user que consume la app.
 * Separado para reutilizar entre signIn + getCurrentUser.
 */
async function _hydrateUserFromAsesor(email) {
  const { data: asesor, error } = await getSupabase()
    .from("asesores")
    .select("*")
    .ilike("email", String(email || "").trim())
    .maybeSingle();
  if (error || !asesor) return null;
  return {
    id: asesor.id,
    email: asesor.email,
    nombre: asesor.nombre,
    isAdmin: asesor.rol === "admin" || email === "admin@tepeyac.mx",
    ...asesor,
  };
}

/**
 * Login.
 * - Si `password` viene, usa Supabase Auth (email + password). Al éxito, hidrata
 *   desde `asesores` para traer rol/campus y guarda en localStorage (compat).
 * - Si `password` no viene (legacy quick-access), busca solo en asesores.
 *   Solo funciona si el email existe y está activo en la tabla.
 * @param {string} email
 * @param {string} [password]
 */
export async function signIn(email, password = null) {
  const client = getSupabase();
  const cleanEmail = String(email || "").trim();
  if (!cleanEmail) return { data: null, error: { message: "Email requerido" } };

  // Ruta Supabase Auth (con contraseña real)
  if (password) {
    const { data: authData, error: authError } = await client.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });
    if (authError) {
      return { data: null, error: { message: authError.message || "Credenciales inválidas" } };
    }
    const hydrated = await _hydrateUserFromAsesor(cleanEmail);
    if (!hydrated) {
      return {
        data: null,
        error: {
          message: "El usuario existe en Auth pero no está en la tabla asesores. Contacta al administrador.",
        },
      };
    }
    // Adjuntar el id de auth para rastreo
    hydrated.auth_id = authData.user?.id || null;
    localStorage.setItem("colegiosyh_user", JSON.stringify(hydrated));
    return { data: { user: hydrated }, error: null };
  }

  // Ruta legacy (quick access — email solo)
  const { data: asesor, error } = await client
    .from("asesores")
    .select("*")
    .eq("email", cleanEmail)
    .eq("activo", true)
    .maybeSingle();

  if (error) return { data: null, error };
  if (!asesor) {
    return { data: null, error: { message: "Email no encontrado en el sistema" } };
  }

  const user = {
    id: asesor.id,
    email: asesor.email,
    nombre: asesor.nombre,
    isAdmin: asesor.rol === "admin" || cleanEmail === "admin@tepeyac.mx",
    ...asesor,
  };

  localStorage.setItem("colegiosyh_user", JSON.stringify(user));
  return { data: { user }, error: null };
}

/** Cierra sesión (tanto Supabase Auth como legacy). */
export async function signOut() {
  try {
    await getSupabase().auth.signOut();
  } catch {
    // ignorar si no había sesión de auth
  }
  localStorage.removeItem("colegiosyh_user");
  return { error: null };
}

/**
 * Helper: corre una promesa con timeout. Si el timeout expira, resuelve con fallback.
 * Evita que la app se cuelgue si Supabase Auth tarda por network/token issues.
 */
function _withTimeout(promise, ms, fallback) {
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      resolve(fallback);
    }, ms);
    promise.then(
      (v) => { if (done) return; done = true; clearTimeout(timer); resolve(v); },
      () => { if (done) return; done = true; clearTimeout(timer); resolve(fallback); }
    );
  });
}

/**
 * Obtiene usuario actual.
 * 1) Primero revisa si hay sesión de Supabase Auth activa → hidrata desde asesores.
 * 2) Si no, fallback a localStorage (legacy quick-access).
 * Ambas rutas tienen timeouts para garantizar que la app jamás se cuelgue.
 */
export async function getCurrentUser() {
  const client = getSupabase();

  // 1) Ruta Supabase Auth (con timeout de 2s para evitar cuelgues)
  try {
    const sessionResult = await _withTimeout(
      client.auth.getSession(),
      2000,
      { data: { session: null } }
    );
    const authUser = sessionResult?.data?.session?.user;
    if (authUser?.email) {
      const hydrated = await _withTimeout(
        _hydrateUserFromAsesor(authUser.email),
        3000,
        null
      );
      if (hydrated) {
        hydrated.auth_id = authUser.id;
        try {
          localStorage.setItem("colegiosyh_user", JSON.stringify(hydrated));
        } catch { /* ignore */ }
        return { user: hydrated, error: null };
      }
    }
  } catch (err) {
    console.warn("[auth] getCurrentUser auth check failed:", err?.message);
  }

  // 2) Fallback legacy localStorage (instantáneo)
  try {
    const raw = localStorage.getItem("colegiosyh_user");
    if (!raw) return { user: null, error: null };
    return { user: JSON.parse(raw), error: null };
  } catch {
    return { user: null, error: null };
  }
}

// ═══════════════════════════════════════════════════
// ASESORES
// ═══════════════════════════════════════════════════

/** Lista asesores activos. Usa: TABLE asesores */
export async function getAsesores() {
  const { data, error } = await getSupabase()
    .from("asesores")
    .select("*")
    .eq("activo", true)
    .order("nombre");
  return { data: data ?? [], error };
}
